import {
  addMonths,
  MAX_HORIZON_YEARS,
  MC_PATH_PRESETS,
  type AbsoluteRateSpec,
  type Assumptions,
  type MonthIndex,
  type RateSpec,
  type Scheduled,
} from '@finsim/model';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import { $project, commandIssued, RateField } from '../../entities/project';
import { currencySymbol } from '../../shared/lib/format';
import { useLocale } from '../../shared/lib/i18n/use-locale';
import { Field, IntegerField, MoneyField, MonthField, Section, Select } from '../../shared/ui';
import { CategoriesSection } from './categories';
import { GoalsSection } from './goals';
import { KeyDatesSection } from './key-dates';

const CURRENCIES = ['EUR', 'BGN', 'USD', 'GBP', 'CHF'] as const;

/** The global CPI may not be CPI-relative (in any period). */
function toAbsolute(s: Scheduled<RateSpec>): Scheduled<AbsoluteRateSpec> | null {
  const isAbsolute = (v: RateSpec): v is AbsoluteRateSpec => v.kind !== 'cpiRelative';
  if (s.kind === 'constant')
    return isAbsolute(s.value) ? { kind: 'constant', value: s.value } : null;
  const segments: { from: MonthIndex; value: AbsoluteRateSpec }[] = [];
  for (const seg of s.segments) {
    if (!isAbsolute(seg.value)) return null;
    segments.push({ ...seg, value: seg.value });
  }
  return { kind: 'schedule', segments };
}

/** Global assumptions shared by every variant. */
export function AssumptionsInspector() {
  const { t } = useTranslation();
  const locale = useLocale();
  const [project, issue] = useUnit([$project, commandIssued]);
  if (!project) return null;
  const a = project.assumptions;
  const set = (assumptions: Assumptions, field: string): void => {
    issue({
      command: { type: 'assumptions/update', assumptions },
      coalesceKey: `assumptions:${field}`,
    });
  };
  const overdraft: Scheduled<RateSpec> = a.overdraftRate ?? {
    kind: 'constant',
    value: { kind: 'fixed', rate: 0 },
  };

  return (
    <div className="min-h-0 flex-1 overflow-y-auto">
      <header className="px-4 pt-3 pb-2">
        <h2 className="text-base font-medium">{t('assumptions.title')}</h2>
        <p className="mt-0.5 text-xs text-text-3">{t('assumptions.description')}</p>
      </header>
      <Section title={t('inspector.sections.general')}>
        <Field label={t('assumptions.start')}>
          {({ id }) => (
            <MonthField
              id={id}
              aria-label={t('assumptions.start')}
              // The start month is edited as an offset from a fixed 1990-01 origin.
              start={'1990-01' as Assumptions['start']}
              value={
                ((Number(a.start.slice(0, 4)) - 1990) * 12 +
                  Number(a.start.slice(5, 7)) -
                  1) as MonthIndex
              }
              max={(2100 - 1990) * 12 - 1}
              onCommit={(offset) => {
                set({ ...a, start: addMonths('1990-01' as Assumptions['start'], offset) }, 'start');
              }}
            />
          )}
        </Field>
        <Field label={t('assumptions.horizon')}>
          {({ id }) => (
            <IntegerField
              id={id}
              value={a.horizonMonths / 12}
              min={1}
              max={MAX_HORIZON_YEARS}
              onCommit={(years) => {
                set({ ...a, horizonMonths: years * 12 }, 'horizon');
              }}
            />
          )}
        </Field>
        <Field label={t('assumptions.startingCash')}>
          {({ id }) => (
            <MoneyField
              id={id}
              value={a.startingCash}
              min={-1e12}
              symbol={currencySymbol(a.currency, locale)}
              onCommit={(startingCash) => {
                set({ ...a, startingCash }, 'startingCash');
              }}
            />
          )}
        </Field>
        <Field label={t('assumptions.currency')}>
          {({ id }) => (
            <Select
              id={id}
              value={a.currency}
              options={(CURRENCIES.includes(a.currency as (typeof CURRENCIES)[number])
                ? CURRENCIES
                : [...CURRENCIES, a.currency]
              ).map((c) => ({ value: c, label: `${c} · ${currencySymbol(c, locale)}` }))}
              onValueChange={(currency) => {
                set({ ...a, currency }, 'currency');
              }}
            />
          )}
        </Field>
      </Section>
      <KeyDatesSection />
      <Section title={t('inspector.sections.inflation')}>
        <RateField
          label={t('assumptions.cpi')}
          value={a.cpi}
          project={project}
          allowCpiRelative={false}
          onCommit={(cpi) => {
            const absolute = toAbsolute(cpi);
            if (absolute) set({ ...a, cpi: absolute }, 'cpi');
          }}
        />
        <RateField
          label={t('assumptions.overdraft')}
          hint={t('assumptions.overdraftHint')}
          value={overdraft}
          project={project}
          onCommit={(rate) => {
            const { overdraftRate: _, ...rest } = a;
            const none =
              rate.kind === 'constant' && rate.value.kind === 'fixed' && rate.value.rate === 0;
            set(none ? rest : { ...a, overdraftRate: rate }, 'overdraft');
          }}
        />
      </Section>
      <CategoriesSection />
      <GoalsSection />
      <Section title={t('inspector.sections.advanced')}>
        <Field label={t('assumptions.mcPaths')}>
          {({ id }) => (
            <Select
              id={id}
              value={String(a.mcPaths)}
              options={[...new Set([...MC_PATH_PRESETS, a.mcPaths])].map((n) => ({
                value: String(n),
                label: n.toLocaleString(locale === 'bg' ? 'bg-BG' : 'en-GB'),
              }))}
              onValueChange={(v) => {
                set({ ...a, mcPaths: Number(v) }, 'mcPaths');
              }}
            />
          )}
        </Field>
        <Field label={t('assumptions.seed')}>
          {({ id }) => (
            <IntegerField
              id={id}
              value={a.seed}
              min={0}
              max={0xffffffff}
              onCommit={(seed) => {
                set({ ...a, seed }, 'seed');
              }}
            />
          )}
        </Field>
      </Section>
    </div>
  );
}
